import React, { useEffect, useMemo, useState } from 'react';
import './Transactions.scss';
import { connect } from 'react-redux';
import {
  getTransactionsActionRequest,
  filterTransactionsAction,
  sortTransactionsAction,
  updateQuickFilterAction
} from '../../store/transaction/transactionActions';
import MercurXTransactionTable 
from '../../components/MercurXTransactionTable/MercurXTransactionTable';
import MercurXDateRangePicker
from '../../components/MercurXDateRangePicker/MercurXDateRangePicker';
import { Col, Form, Row, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { transactionsQuickFilterConstants } from './transactionsConstants';

const ALL_ACTIONS_VALUE = '__all_actions__';
const PRIORITY_ACTIONS = ['buy', 'staking', 'mint'];

const formatActionLabel = (action) =>
  action
    .split(/[_-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

function Transactions({
  getTransactionsRequest,
  transactions,
  quickFilter,
  updateQuickFilter,
  sortTransactions,
  filterTransactions,
}) {
  const [filterInput, setFilterInput] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [selectedAction, setSelectedAction] = useState(ALL_ACTIONS_VALUE);

  const actionOptions = useMemo(() => {
    const actionSet = new Set(
      (transactions || [])
        .map((item) => item?.action?.toString().trim().toLowerCase())
        .filter(Boolean)
    );

    const orderedActions = PRIORITY_ACTIONS.filter((action) => actionSet.has(action));
    const additionalActions = [...actionSet]
      .filter((action) => !PRIORITY_ACTIONS.includes(action))
      .sort((actionA, actionB) => actionA.localeCompare(actionB));

    return [ALL_ACTIONS_VALUE, ...orderedActions, ...additionalActions];
  }, [transactions]);

  useEffect(() => {
    getTransactionsRequest();
    updateQuickFilter(transactionsQuickFilterConstants.ALL);
  }, [getTransactionsRequest, updateQuickFilter]);

  useEffect(() => {
    filterTransactions({
      search: filterInput,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      action: selectedAction === ALL_ACTIONS_VALUE ? '' : selectedAction,
    });
    sortTransactions();
  }, [
    dateRange.endDate,
    dateRange.startDate,
    filterInput,
    filterTransactions,
    quickFilter,
    selectedAction,
    sortTransactions,
    transactions,
  ]);

  useEffect(() => {
    if (selectedAction !== ALL_ACTIONS_VALUE && !actionOptions.includes(selectedAction)) {
      setSelectedAction(ALL_ACTIONS_VALUE);
    }
  }, [actionOptions, selectedAction]);

  return (
    <>
      <div className="text-fs-head-md mb-4">Transactions</div>
      <div className="transactions-filter-panel mt-4">
        <div className="filter-panel-title">Filters</div>
        <Row className="g-3 align-items-end">
          <Col lg={8}>
            <Form.Label className="filter-control-label">Text Search</Form.Label>
            <Form.Control
              className="text-fs-body-sm filter-control-input"
              type="text"
              placeholder="Search by project name, address or txn hash..."
              aria-label="Transaction text search"
              aria-describedby="basic-addon1"
              value={filterInput}
              onChange={(e) => setFilterInput(e.target.value)}
              size={'sm'}
            />
          </Col>
          <Col lg={4}>
            <MercurXDateRangePicker
              label="Txn Date Range"
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={setDateRange}
            />
          </Col>
          <Col lg={12}>
            <Form.Label className="filter-control-label">Action</Form.Label>
            <div className="action-filter-group">
              {actionOptions.map((actionOption) => {
                const isActive = selectedAction === actionOption;
                const label = actionOption === ALL_ACTIONS_VALUE
                  ? 'All Actions'
                  : formatActionLabel(actionOption);

                return (
                  <button
                    key={actionOption}
                    type="button"
                    className={`action-filter-chip${isActive ? ' is-active' : ''}`}
                    onClick={() => setSelectedAction(actionOption)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </Col>
        </Row>
      </div>
      <Row
        id="transactions-container"
        className="d-flex align-items-center justify-content-end mt-4"
      >
        <Col className="text-md-end">
        <ToggleButtonGroup size={'sm'} type="radio" name="options" defaultValue={1}>
            <ToggleButton
              size={'sm'}
              variant={quickFilter === transactionsQuickFilterConstants.ALL ? 'primary' : 'light'}
              className={
                quickFilter !== transactionsQuickFilterConstants.ALL ? 'text-t-body-color' : ''
              }
              id="tbg-radio-1"
              value={transactionsQuickFilterConstants.ALL}
              onClick={() => updateQuickFilter(transactionsQuickFilterConstants.ALL)}
            >
              All
            </ToggleButton>
            <ToggleButton
              size={'sm'}
              variant={quickFilter === transactionsQuickFilterConstants.TRUE ? 'primary' : 'light'}
              className={
                quickFilter !== transactionsQuickFilterConstants.TRUE ? 'text-t-body-color' : ''
              }
              id="tbg-radio-2"
              value={transactionsQuickFilterConstants.TRUE}
              onClick={() => updateQuickFilter(transactionsQuickFilterConstants.TRUE)}
            >
              True
            </ToggleButton>
            <ToggleButton
              size={'sm'}
              variant={quickFilter === transactionsQuickFilterConstants.FALSE ? 'primary' : 'light'}
              className={
                quickFilter !== transactionsQuickFilterConstants.FALSE ? 'text-t-body-color' : ''
              }
              id="tbg-radio-3"
              value={transactionsQuickFilterConstants.FALSE}
              onClick={() => updateQuickFilter(transactionsQuickFilterConstants.FALSE)}
            >
              False
            </ToggleButton>
          </ToggleButtonGroup>
        </Col>
      </Row>
      <div id="transactions-container">
        <MercurXTransactionTable />
      </div>
      
    </>
  );
}
const mapStateToProps = (state) => {
  return {
    transactions: state.transactionReducer.transactions,
    quickFilter: state.transactionReducer.quickFilter,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    getTransactionsRequest: (payload) => {
      dispatch(getTransactionsActionRequest(payload));
    },
    updateQuickFilter: (payload) => {
      dispatch(updateQuickFilterAction(payload));
    },
    sortTransactions: (payload) => {
      dispatch(sortTransactionsAction(payload));
    },
    filterTransactions: (payload) => {
      dispatch(filterTransactionsAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Transactions);
